import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { parseFloatFromString, formatCurrency } from '../../utils/formatters';
import { usePositionContext } from '../../contexts/PositionContext';
import { SEARCH_TOKENS } from '../../graphql/queries/tokens';

const TokenSelector = ({ 
  selectedTokenId,
  selectedCoingeckoApiId, // New prop for tokens without tokenId
  selectedToken, // Full token object from allocation plan
  onSelectToken,
  index,
  isActive,
  toggleActive,
  existingTokenIds = [],
  existingCoingeckoApiIds = []
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredResults, setFilteredResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  
  const { positions } = usePositionContext();
  
  const [searchTokens, { loading: searchLoading }] = useLazyQuery(SEARCH_TOKENS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'ignore'
  });

  // Helper function to create result items
  const createResultItem = useCallback((token, position = null, isFromSearch = false) => {
    return {
      id: token.id || `search-${token.coingeckoApiId}`,
      name: token.name,
      ticker: token.ticker,
      coingeckoApiId: token.coingeckoApiId || null,
      coingeckoSlug: token.coingeckoSlug || null, // Only available for imported tokens
      hasPosition: !!position,
      isFromSearch,
      tokenId: token.id,
      currentPrice: token.marketData?.currentPrice || null,
      marketCapRank: token.marketCapRank || null,
      // Position-specific data
      holdingsValue: position?.holdingsValue || null,
      roi: position?.current?.roi || null
    };
  }, []);

  // Get tokens from positions
  const positionTokens = useCallback(() => {
    if (!positions) return [];
    
    return positions
      .filter(p => p.token) // Ensure token exists
      .map(position => createResultItem(position.token, position))
      .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically
  }, [positions, createResultItem]);

  // Filter position tokens locally based on search query
  const filterPositionTokens = useCallback((term) => {
    const tokens = positionTokens();
    if (!term) return tokens;
    
    const lowerTerm = term.toLowerCase();
    return tokens.filter(item => 
      (item.name.toLowerCase().includes(lowerTerm) || 
       item.ticker.toLowerCase().includes(lowerTerm)) &&
      // Exclude already selected tokens
      (item.tokenId === selectedTokenId || !existingTokenIds.includes(item.tokenId))
    );
  }, [positionTokens, selectedTokenId, existingTokenIds]);

  // API search for tokens not in positions
  const performApiSearch = useCallback(async (term) => {
    if (term.length <= 2) return [];
    
    try {
      const result = await searchTokens({ 
        variables: { query: term } 
      });
      
      if (result.data?.searchTokens) {
        return result.data.searchTokens.map(token => {
          const mappedToken = {
            id: token.tokenId, // May be null for tokens not yet imported
            name: token.name,
            ticker: token.ticker,
            coingeckoApiId: token.coingeckoApiId,
            marketCapRank: token.marketCapRank
          };
          return createResultItem(mappedToken, null, true);
        });
      }
    } catch (error) {
      console.error('Token search error:', error);
    }
    
    return [];
  }, [searchTokens, createResultItem]);

  // Get the currently selected token/result
  const getSelectedToken = useCallback(() => {
    // If we have a full token object from allocation plan, use it
    if (selectedToken) {
      const posToken = positionTokens().find(t => t.tokenId === selectedToken.id);
      if (posToken) {
        // Use position data if available (shows holdings info)
        return posToken;
      } else {
        // Use token data but without position info
        return {
          id: selectedToken.id,
          name: selectedToken.name,
          ticker: selectedToken.ticker,
          coingeckoApiId: selectedToken.coingeckoApiId,
          tokenId: selectedToken.id,
          isFromSearch: !selectedToken.id // Mark as "New" if no tokenId
        };
      }
    }
    
    if (selectedTokenId) {
      // Find by tokenId in positions first
      const posToken = positionTokens().find(t => t.tokenId === selectedTokenId);
      if (posToken) return posToken;
    }
    
    if (selectedCoingeckoApiId) {
      // This would be for tokens selected from search that don't have tokenId yet
      return {
        id: `search-${selectedCoingeckoApiId}`,
        name: 'Unknown Token', // We'd need to store this info somehow
        ticker: 'N/A',
        coingeckoApiId: selectedCoingeckoApiId,
        isFromSearch: true
      };
    }
    
    return null;
  }, [selectedToken, selectedTokenId, selectedCoingeckoApiId, positionTokens]);

  // Main search effect - similar to Jump.js
  useEffect(() => {
    if (!isActive) return;
    
    const processSearch = async () => {
      setIsSearching(true);
      
      // Always filter local position tokens first
      const localResults = filterPositionTokens(searchQuery);
      
      // If search term is short, only show local results
      if (searchQuery.length <= 2) {
        setFilteredResults(localResults);
        setIsSearching(false);
        return;
      }
      
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Debounce API search for 1 second
      searchTimeoutRef.current = setTimeout(async () => {
        const apiResults = await performApiSearch(searchQuery);
        
        // Merge results: position tokens first, then new API results
        const localTokenIds = new Set(localResults.map(r => r.tokenId).filter(Boolean));
        const localCoingeckoApiIds = new Set(localResults.map(r => r.coingeckoApiId).filter(Boolean));
        
        // Also include already used tokens from allocations (passed as props)
        const allExistingTokenIds = new Set([...localTokenIds, ...existingTokenIds]);
        const allExistingCoingeckoApiIds = new Set([...localCoingeckoApiIds, ...existingCoingeckoApiIds]);
        
        const newApiResults = apiResults.filter(result => {
          // Filter out if tokenId matches (when both exist)
          if (result.tokenId && allExistingTokenIds.has(result.tokenId)) {
            return false;
          }
          // Filter out if coingeckoApiId matches
          if (result.coingeckoApiId && allExistingCoingeckoApiIds.has(result.coingeckoApiId)) {
            return false;
          }
          return true;
        });
        
        const mergedResults = [...localResults, ...newApiResults];
        setFilteredResults(mergedResults);
        setIsSearching(false);
      }, 1000);
    };
    
    processSearch();
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, isActive, filterPositionTokens, performApiSearch, existingCoingeckoApiIds, existingTokenIds]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isActive && 
          dropdownRef.current && 
          !dropdownRef.current.contains(event.target) &&
          inputRef.current &&
          !inputRef.current.contains(event.target)) {
        toggleActive(null); // Close dropdown
        setSearchQuery(''); // Clear search when closing
      }
    };

    if (isActive) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActive, toggleActive]);

  // Handle Esc key to close dropdown
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isActive && event.key === 'Escape') {
        toggleActive(null); // Close dropdown
        setSearchQuery(''); // Clear search when closing
      }
    };

    if (isActive) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, toggleActive]);

  // Position dropdown when it becomes active and reset search
  useEffect(() => {
    if (isActive) {
      // Reset search and results when dropdown opens
      setSearchQuery('');
      const initialResults = filterPositionTokens('');
      setFilteredResults(initialResults);
      setHighlightedIndex(initialResults.length ? 0 : -1);
      
      if (inputRef.current && dropdownRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        
        // Position dropdown relative to viewport
        dropdownRef.current.style.position = 'fixed';
        dropdownRef.current.style.top = `${rect.bottom}px`;
        dropdownRef.current.style.left = `${rect.left}px`;
        dropdownRef.current.style.width = '300px';
        dropdownRef.current.style.maxHeight = '300px';
      }
    }
  }, [isActive, filterPositionTokens]);

  useEffect(() => {
    if (!isActive) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex((prev) => {
      if (filteredResults.length === 0) return -1;
      if (prev < 0) return 0;
      if (prev >= filteredResults.length) return filteredResults.length - 1;
      return prev;
    });
  }, [filteredResults, isActive]);

  const handleSelectToken = (result) => {
    // Pass tokenId, coingeckoApiId, and coingeckoSlug (if available) to parent
    onSelectToken({
      tokenId: result.tokenId,
      coingeckoApiId: result.coingeckoApiId,
      coingeckoSlug: result.coingeckoSlug, // Only available for imported tokens, undefined for search results
      name: result.name,
      ticker: result.ticker
    }, index);
    setSearchQuery(''); // Clear search after selection
    toggleActive(null); // Close the dropdown
  };

  const handleKeyNavigation = (event) => {
    if (!isActive) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        if (filteredResults.length === 0) return -1;
        const next = prev + 1;
        return next >= filteredResults.length ? filteredResults.length - 1 : next;
      });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        if (filteredResults.length === 0) return -1;
        const next = prev - 1;
        return next < 0 ? 0 : next;
      });
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredResults.length) {
        handleSelectToken(filteredResults[highlightedIndex]);
      }
    }
  };

  const currentSelectedToken = getSelectedToken();

  return (
    <div className="relative">
      <div 
        ref={inputRef}
        onClick={() => toggleActive(index)}
        className="flex items-center cursor-pointer p-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded w-full min-w-0"
      >
        {currentSelectedToken ? (
          <span className="truncate">
            {currentSelectedToken.name} ({currentSelectedToken.ticker})
            {currentSelectedToken.isFromSearch && (
              <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 px-1 rounded">
                New
              </span>
            )}
          </span>
        ) : (
          <span className="text-gray-400">Select token...</span>
        )}
      </div>
      
      {isActive && (
        <div 
          ref={dropdownRef}
          className="z-50 bg-white dark:bg-gray-700 border dark:border-gray-600 rounded-md shadow-lg overflow-auto"
          style={{
            position: 'fixed',
            maxHeight: '300px'
          }}
        >
          <div className="p-2 sticky top-0 bg-white dark:bg-gray-700 border-b dark:border-gray-600">
            <div className="flex items-center">
              <input
                autoFocus
                type="text"
                placeholder="Search tokens..."
                className="w-full p-2 border dark:border-gray-600 bg-gray-50 dark:bg-gray-800 rounded dark:text-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleKeyNavigation}
              />
              {(isSearching || searchLoading) && (
                <div className="ml-2">
                  <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}
            </div>
          </div>
          <ul>
            {filteredResults.map((result, resultIndex) => {
              const isHighlighted = resultIndex === highlightedIndex;
              return (
              <li 
                key={result.id}
                className={`px-4 py-2 cursor-pointer dark:text-white ${
                  isHighlighted ? 'bg-gray-100 dark:bg-gray-600' : 'hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
                onClick={() => handleSelectToken(result)}
                onMouseEnter={() => setHighlightedIndex(resultIndex)}
              >
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span>{result.name} ({result.ticker})</span>
                    </div>
                    {result.hasPosition ? (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Holdings: {formatCurrency(result.holdingsValue)} • ROI: {result.roi}
                      </div>
                    ) : (
                      result.marketCapRank && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          MC Rank: #{result.marketCapRank}
                        </div>
                      )
                    )}
                  </div>
                  {result.currentPrice && (
                    <span className="text-gray-500 dark:text-gray-400 ml-2">
                      ${parseFloatFromString(result.currentPrice).toLocaleString()}
                    </span>
                  )}
                </div>
              </li>
            )})}
            {filteredResults.length === 0 && !isSearching && (
              <li className="px-4 py-2 text-gray-500 dark:text-gray-400">
                {searchQuery ? 'No tokens found' : 'Type to search for tokens'}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default TokenSelector; 